import { useMemo, useRef, useCallback, useEffect, useState } from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import { useImageStore } from '@/entities/image/store';
import type { ImageFile } from '@/shared/types';

const BASE_ITEM_SIZE = 140;
const GAP = 8;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 3.0;
const ZOOM_STEP = 0.1;
export default function ImageGrid() {
  const images = useImageStore((s) => s.images);
  const searchQuery = useImageStore((s) => s.searchQuery);
  const filtered = useMemo(() => {
    if (!searchQuery) return images;
    const q = searchQuery.toLowerCase();
    return images.filter((img) => img.name.toLowerCase().includes(q));
  }, [images, searchQuery]);
  
  const selectedIndices = useImageStore((s) => s.selectedIndices);
  const selectedIndex = useImageStore((s) => s.selectedIndex);
  const toggleSelect = useImageStore((s) => s.toggleSelect);
  const setSelectedIndex = useImageStore((s) => s.setSelectedIndex);
  const setExif = useImageStore((s) => s.setExif);
  const setActiveImagePath = useImageStore((s) => s.setActiveImagePath);
  const llmLoadingPaths = useImageStore((s) => s.llmLoadingPaths);
  const setViewingImagePath = useImageStore((s) => s.setViewingImagePath);
  const viewingImagePath = useImageStore((s) => s.viewingImagePath);

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(800);
  const [containerHeight, setContainerHeight] = useState(1080);
  const [scrollTop, setScrollTop] = useState(0);
  const [zoom, setZoom] = useState(1.0);

  // Ctrl + wheel zoom handler
  const handleWheel = useCallback((e: WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
      setZoom(prev => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prev + delta)));
    }
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) { 
        setContainerWidth(entry.contentRect.width); 
        setContainerHeight(entry.contentRect.height); 
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const ITEM_SIZE = BASE_ITEM_SIZE * zoom;
  const columns = useMemo(() => Math.max(1, Math.floor((containerWidth + GAP) / (ITEM_SIZE + GAP))), [containerWidth, ITEM_SIZE]);
  const rows = useMemo(() => Math.ceil(filtered.length / columns), [filtered.length, columns]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only run if viewer is closed and no input is focused
      if (viewingImagePath || ['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName || '')) return;

      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', ' ', 'Enter'].includes(e.key)) {
        e.preventDefault();
      }

      let nextIdx = selectedIndex ?? 0;

      if (e.key === 'ArrowLeft') nextIdx = Math.max(0, nextIdx - 1);
      else if (e.key === 'ArrowRight') nextIdx = Math.min(filtered.length - 1, nextIdx + 1);
      else if (e.key === 'ArrowUp') nextIdx = Math.max(0, nextIdx - columns);
      else if (e.key === 'ArrowDown') nextIdx = Math.min(filtered.length - 1, nextIdx + columns);
      else if (e.key === ' ') {
        // Toggle behavior used ctrlKey pattern in store
        if (selectedIndex !== null) toggleSelect(selectedIndex, true, false);
        return;
      } else if (e.key === 'Enter') {
        if (selectedIndex !== null && filtered[selectedIndex]) setViewingImagePath(filtered[selectedIndex].path);
        return;
      }

      if (nextIdx !== selectedIndex) {
        setSelectedIndex(nextIdx);
        const img = filtered[nextIdx];
        if (img) {
          setActiveImagePath(img.path);
          // Scroll into view if needed
          const row = Math.floor(nextIdx / columns);
          const y = row * (ITEM_SIZE + GAP);
          const container = containerRef.current;
          if (container) {
            const viewTop = container.scrollTop;
            const viewBottom = viewTop + container.clientHeight;
            if (y < viewTop) {
              container.scrollTo({ top: y, behavior: 'smooth' });
            } else if (y + ITEM_SIZE > viewBottom) {
              container.scrollTo({ top: y - container.clientHeight + ITEM_SIZE + GAP, behavior: 'smooth' });
            }
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex, filtered, columns, viewingImagePath, toggleSelect, setSelectedIndex, setActiveImagePath, setViewingImagePath]);

  const effectiveHeight = Math.max(containerHeight, 4000); 
  const visibleStart = Math.floor(scrollTop / (ITEM_SIZE + GAP));
  const visibleEnd = Math.min(filtered.length, Math.ceil((scrollTop + effectiveHeight) / (ITEM_SIZE + GAP)) * columns);
  
  const visibleItems = useMemo(() => {
    const items: { image: ImageFile; index: number; row: number; col: number }[] = [];
    for (let i = Math.max(0, visibleStart * columns); i < visibleEnd; i++) {
      if (i >= filtered.length) break;
      items.push({ image: filtered[i], index: i, row: Math.floor(i / columns), col: i % columns });
    }
    return items;
  }, [visibleStart, visibleEnd, filtered, columns]);

  const handleClick = useCallback((idx: number, e: React.MouseEvent) => {
    toggleSelect(idx, e.ctrlKey || e.metaKey, e.shiftKey);
    setSelectedIndex(idx);
    const img = filtered[idx];
    if (img) {
      setActiveImagePath(img.path);
      if (!useImageStore.getState().imageExif.has(img.path)) {
        window.electronAPI.invoke('exif:read', img.path).then((result: any) => {
          if (result && !result.error) setExif(img.path, result);
        });
      }
    }
  }, [filtered, toggleSelect, setSelectedIndex, setExif, setActiveImagePath]);

  const totalHeight = rows * (ITEM_SIZE + GAP);
  const totalContentWidth = (columns * ITEM_SIZE) + ((columns - 1) * GAP);
  const offsetX = Math.max(0, (containerWidth - totalContentWidth) / 2);

  return (
    <div ref={containerRef} className="flex-1 h-full min-h-0 overflow-y-auto bg-zinc-900 p-2 select-none"
      onScroll={(e) => {
        setScrollTop(e.currentTarget.scrollTop);
        if (e.currentTarget.clientHeight !== containerHeight) setContainerHeight(e.currentTarget.clientHeight);
      }}>
      <div style={{ height: totalHeight, position: 'relative', paddingTop: 0, transition: 'height 0.15s ease-out' }}>
        {visibleItems.map(({ image, index, row, col }) => {
          const isSelected = selectedIndices.has(index);
          const isActive = selectedIndex === index;
          const x = col * (ITEM_SIZE + GAP) + offsetX;
          const y = row * (ITEM_SIZE + GAP);
          const thumbUrl = image.thumbnail || (image.path ? `file://${image.path}` : '');
          const isLoading = llmLoadingPaths.has(image.path);
          
           return (
             <div
               key={image.path}
               onClick={(e) => handleClick(index, e)}
               onDoubleClick={() => setViewingImagePath(image.path)}
               className={`absolute rounded-md overflow-hidden cursor-pointer border-2 transition-all duration-150 ease-out
                 ${isActive ? 'border-blue-500' : isSelected ? 'border-gray-400' : 'border-transparent hover:border-gray-600'}`}
               style={{ left: x, top: y, width: ITEM_SIZE, height: ITEM_SIZE, transitionProperty: 'width, height, left, top' }}
             >
               <img src={thumbUrl} alt={image.name} className="w-full h-full object-cover" loading="lazy" draggable={false} />
               {isSelected && (
                 <div className="absolute inset-0 bg-green-500/30 pointer-events-none" />
               )}
               {isLoading && (
                 <div className="absolute inset-0 z-10 bg-black/50 flex items-center justify-center">
                   <Loader2 className="w-8 h-8 text-white animate-spin" />
                 </div>
               )}
              {useImageStore.getState().hasStagedChanges(image.path) && (
                <div className="absolute top-0 left-0 z-20 flex items-center justify-center p-1">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-zinc-900 shadow-md ring-1 ring-white/10">
                    <AlertTriangle className="w-4 h-4 text-yellow-400" />
                  </div>
                </div>
              )}
               {!isLoading && (
                 <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1 py-0.5 truncate text-xs">
                   {image.name}
                 </div>
               )}
             </div>
           );
        })}
      </div>
      {filtered.length === 0 && (
        <div className="h-full flex flex-col items-center justify-center text-gray-500">
          <p className="text-lg mb-2">No images found</p>
          <p className="text-sm">Open a folder to get started</p>
        </div>
      )}
    </div>
  );
}
